/**
 * Auth Analytics Hook
 *
 * Tracks authentication-related analytics events and user properties.
 * Handles session tracking and retention milestone checks.
 */

import { useEffect } from 'react';
import type { UserProfileResponse } from '@models';
import {
  getOrCreateSessionId,
  getSessionStartedTrackedId,
  markSessionStartedTracked,
} from '@lib/analytics/session';

// =============================================================================
// HOOK OPTIONS
// =============================================================================

export interface UseAuthAnalyticsOptions {
  /** Current user profile from backend */
  user: UserProfileResponse | null;
  /** Whether user is authenticated with Logto */
  logtoIsAuthenticated: boolean;
  /** Whether user data is still loading */
  userLoading: boolean;
  /** Zustand store setCurrentUser */
  setCurrentUser: (user: UserProfileResponse | null) => void;
  /** Set Amplitude user properties */
  setUserProperties: (properties: Record<string, unknown>) => void;
  /** Set Amplitude user ID */
  setUserId: (userId: string) => void;
  /** Track session started event */
  trackSessionStarted: (properties: Record<string, unknown>) => void;
  /** Check retention milestones (day_1, day_7, day_30 returns) */
  checkRetentionMilestones: (signupDate?: string) => void;
}

// =============================================================================
// HOOK IMPLEMENTATION
// =============================================================================

/**
 * Manages analytics tracking for authentication state.
 * Syncs user data to Zustand store and Amplitude.
 */
export function useAuthAnalytics(options: UseAuthAnalyticsOptions): void {
  const {
    user,
    logtoIsAuthenticated,
    userLoading,
    setCurrentUser,
    setUserProperties,
    setUserId,
    trackSessionStarted,
    checkRetentionMilestones,
  } = options;

  useEffect(() => {
    if (user) {
      // Sync user to Zustand store
      setCurrentUser(user);

      // Set Amplitude user properties
      setUserProperties({
        email: user.email,
        user_id: user.id,
        signup_date: user.created_at,
        user_type: 'authenticated',
      });
      setUserId(user.id);

      // Track retention baseline once per session for authenticated users
      // AIDEV-NOTE: Uses sessionStorage (not useRef) to survive React StrictMode
      // double-mounting and HMR remounts in development
      const sessionId = getOrCreateSessionId();
      if (getSessionStartedTrackedId() !== sessionId) {
        markSessionStartedTracked(sessionId);

        const signupMs = user.created_at ? new Date(user.created_at).getTime() : null;
        const daysSinceSignup = signupMs
          ? Math.floor((Date.now() - signupMs) / (1000 * 60 * 60 * 24))
          : null;

        trackSessionStarted({
          user_type: 'authenticated',
          days_since_signup: daysSinceSignup,
        });

        // AIDEV-NOTE: Check retention milestones (day_1_return, day_7_return, day_30_return)
        // Only fires once per session when user is authenticated
        checkRetentionMilestones(user.created_at);
      }
    } else if (!userLoading && !logtoIsAuthenticated) {
      // Set guest user properties for analytics
      setUserProperties({
        user_type: 'guest',
      });

      // Track guest sessions for complete funnel visibility
      const sessionId = getOrCreateSessionId();
      if (getSessionStartedTrackedId() !== sessionId) {
        markSessionStartedTracked(sessionId);

        trackSessionStarted({
          user_type: 'guest',
        });
      }
    }
  }, [
    checkRetentionMilestones,
    logtoIsAuthenticated,
    setCurrentUser,
    setUserId,
    setUserProperties,
    trackSessionStarted,
    user,
    userLoading,
  ]);
}
